// src/services/DeviceStorageService.ts
import type { Device, DeviceWithMetadata } from "@/types/device";

export interface DeviceStorageData {
  devices: DeviceWithMetadata[];
  lastSync: string;
  version: number;
}

export class DeviceStorageService {
  private static readonly STORAGE_KEY = "iot_devices_v2";
  private static readonly VERSION = 2;

  /**
   * Save devices to localStorage
   */
  public static saveDevices(devices: DeviceWithMetadata[]): void {
    try {
      const data: DeviceStorageData = {
        devices,
        lastSync: new Date().toISOString(),
        version: this.VERSION,
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      console.log(`Saved ${devices.length} devices to localStorage`);
    } catch (error) {
      console.warn("Failed to save devices to localStorage:", error);
    }
  }

  /**
   * Load devices from localStorage
   */
  public static loadDevices(): DeviceWithMetadata[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return [];
      }

      const data: DeviceStorageData = JSON.parse(stored);

      // Check version compatibility
      if (data.version !== this.VERSION) {
        console.warn("Storage version mismatch, clearing storage");
        this.clearDevices();
        return [];
      }

      console.log(`Loaded ${data.devices.length} devices from localStorage`);
      return data.devices || [];
    } catch (error) {
      console.warn("Failed to load devices from localStorage:", error);
      return [];
    }
  }

  /**
   * Add or update a device
   */
  public static saveDevice(device: Device): void {
    const devices = this.loadDevices();
    const deviceWithMetadata: DeviceWithMetadata = {
      ...device,
      status: (device as any).status || "unknown",
      lastSeen: (device as any).lastSeen,
    };

    const existingIndex = devices.findIndex((d) => d.id === device.id);

    if (existingIndex >= 0) {
      devices[existingIndex] = deviceWithMetadata;
    } else {
      devices.push(deviceWithMetadata);
    }

    this.saveDevices(devices);
  }

  /**
   * Remove a device
   */
  public static removeDevice(deviceId: number): void {
    const devices = this.loadDevices();
    const filteredDevices = devices.filter((d) => d.id !== deviceId);
    this.saveDevices(filteredDevices);
  }

  /**
   * Update device status
   */
  public static updateDeviceStatus(
    deviceId: number,
    status: "online" | "offline" | "unknown",
    lastSeen?: string
  ): void {
    const devices = this.loadDevices();
    const deviceIndex = devices.findIndex((d) => d.id === deviceId);

    if (deviceIndex >= 0) {
      devices[deviceIndex].status = status;
      if (lastSeen) {
        devices[deviceIndex].lastSeen = lastSeen;
      }
      this.saveDevices(devices);
    }
  }

  /**
   * Update device information
   */
  public static updateDevice(deviceId: number, updates: Partial<Device>): void {
    const devices = this.loadDevices();
    const deviceIndex = devices.findIndex((d) => d.id === deviceId);

    if (deviceIndex >= 0) {
      devices[deviceIndex] = { ...devices[deviceIndex], ...updates };
      this.saveDevices(devices);
    }
  }

  /**
   * Check if device exists
   */
  public static deviceExists(deviceId: number): boolean {
    const devices = this.loadDevices();
    return devices.some((d) => d.id === deviceId);
  }

  /**
   * Get device by ID
   */
  public static getDevice(deviceId: number): DeviceWithMetadata | null {
    const devices = this.loadDevices();
    return devices.find((d) => d.id === deviceId) || null;
  }

  /**
   * Clear all devices
   */
  public static clearDevices(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log("Cleared devices storage");
    } catch (error) {
      console.warn("Failed to clear devices storage:", error);
    }
  }

  /**
   * Get storage stats
   */
  public static getStorageStats(): {
    deviceCount: number;
    lastSync: string | null;
    storageSize: number;
  } {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return {
          deviceCount: 0,
          lastSync: null,
          storageSize: 0,
        };
      }

      const data: DeviceStorageData = JSON.parse(stored);
      return {
        deviceCount: data.devices.length,
        lastSync: data.lastSync,
        storageSize: new Blob([stored]).size,
      };
    } catch (error) {
      console.warn("Failed to get storage stats:", error);
      return {
        deviceCount: 0,
        lastSync: null,
        storageSize: 0,
      };
    }
  }

  /**
   * Migrate old storage format
   */
  public static migrateOldStorage(): number {
    try {
      const oldKey = "iot_devices";
      const oldData = localStorage.getItem(oldKey);

      if (!oldData) return 0;

      const oldDevices = JSON.parse(oldData);
      if (Array.isArray(oldDevices)) {
        const migratedDevices: DeviceWithMetadata[] = oldDevices.map(
          (device) => ({
            ...device,
            status: device.status || "unknown",
            lastSeen: device.lastSeen,
          })
        );

        this.saveDevices(migratedDevices);
        localStorage.removeItem(oldKey);

        console.log(
          `Migrated ${migratedDevices.length} devices from old storage`
        );
        return migratedDevices.length;
      }
    } catch (error) {
      console.warn("Failed to migrate old storage:", error);
    }

    return 0;
  }
}

export default DeviceStorageService;
